import { Input } from '@/components/ui/input';
import { Loader2, MapPin } from 'lucide-react';
import { useAddressAutocomplete } from '@/hooks/useAddressAutocomplete';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const AddressAutocomplete = ({ value, onChange, placeholder = 'Start typing an address...', className }: AddressAutocompleteProps) => {
  const { open, suggestions, isLoading, wrapperRef, handleChange, selectSuggestion, handleFocus } = useAddressAutocomplete(value, onChange);

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={value}
        onChange={e => handleChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        onFocus={handleFocus}
        // Native browser tooltip on hover surfaces the full address even
        // when it gets visually clipped inside a narrow flex slot.
        title={value || undefined}
      />
      {isLoading && (
        <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground animate-spin" />
      )}
      {open && suggestions.length > 0 && (
        // The dropdown sits absolute under the input. We let it grow
        // wider than the input (`min-w-full max-w-[28rem]`) so long
        // addresses like "Via dei Foderari 12, 00186 Roma RM, Italy"
        // are fully readable without truncation, even when the input
        // itself is squeezed inside a flex row with a narrow type
        // selector and a trash button.
        <div className="absolute z-50 mt-1 min-w-full max-w-[28rem] rounded-md border border-border bg-popover shadow-md overflow-hidden">
          {suggestions.map((s, i) => (
            <button
              key={s.placeId ?? `${s.label}-${i}`}
              type="button"
              className="flex w-full items-start gap-2 px-3 py-2 text-sm text-popover-foreground hover:bg-accent transition-colors text-left"
              onClick={() => selectSuggestion(s)}
              title={s.label}
            >
              <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <span className="break-words">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
